(ns dashboard.views.questionnaire.question-lab.events
  (:require [clojure.string :as str]
            [dashboard.components.confirm-unsaved-answer :refer [confirm-unsaved-answer]]
            [dashboard.entity.analysis.db :as analysis.db]
            [dashboard.entity.analysis.modals :as analysis.view]
            [dashboard.entity.question.answer-scope :as answer-scope]
            [dashboard.entity.question.analysis-search :as question.analysis.search]
            [dashboard.notifications :as notifications]
            [dashboard.services.answers :as answers]
            [dashboard.services.http-config :as http-config]
            [dashboard.ui :as ui]
            [dashboard.views.questionnaire.question-lab.common :as common]
            [re-frame.core :as rf]))

(defn make-edit-state [question answer linked-applied-analyses]
  (let [comment-required (:comment-required question)
        {:keys [comment] :as attributes} (common/answer->edit-state answer linked-applied-analyses)
        edit-state (if (and (not comment-required)
                            (not (str/blank? comment)))
                     ;; If comment is not required, but there is a comment in the answer, append it to the response, so we don't loose the comment
                     (-> attributes
                         (update :response (fn [response]
                                             (str response "\n\n" comment)))
                         (dissoc :comment))
                     attributes)]
    (assoc edit-state :dirty? false)))

(def empty-edit-state
  {:response ""
   :comment ""
   :analysis-content ""
   :dirty? false})

(rf/reg-event-fx
 ::set-question-index
 (fn [{:keys [db]} [_ questionnaire-id index confirm-changes?]]
   (let [question-ids (common/get-question-ids db questionnaire-id)
         question-id (nth question-ids index)
         editor-dirty? (common/editor-dirty? db questionnaire-id)]
     (if (and confirm-changes? editor-dirty?)
       {:dispatch [::ui/push-modal {:component (confirm-unsaved-answer {:on-confirm #(rf/dispatch [::set-question-index questionnaire-id index false])})}]}
       {:db (assoc-in db [:question-lab questionnaire-id :question-index] index)
        :fx [[:dispatch [::answers/get-answers questionnaire-id question-id]]
             (let [question (first (filter #(= question-id (:id %))
                                           (get-in db [:questionnaires questionnaire-id :questions :data])))
                   selected-answer (:selected-answer question)
                   edit-state (if (and selected-answer
                                       (= (:source selected-answer) "human"))
                                (make-edit-state question
                                                 selected-answer
                                                 (analysis.db/db->applied-analyses-for-answer
                                                  db
                                                  (:id selected-answer)))
                                ;; Leave answer inputs empty for non-human answer (inference search results)
                                empty-edit-state)]
               [:dispatch [::set-edit-answer-state questionnaire-id edit-state]])]}))))

(rf/reg-event-fx
  ::set-edit-answer-state-field
  (fn [{:keys [db]} [_ questionnaire-id field value]]
    {:db (-> db
             (assoc-in [:question-lab questionnaire-id :edit-answer-state field] value)
             (assoc-in [:question-lab questionnaire-id :edit-answer-state :dirty?] true))}))

(rf/reg-event-fx
  ::update-edit-answer-state-field
  (fn [{:keys [db]} [_ questionnaire-id field op & args]]
    {:db (-> db
             (update-in [:question-lab questionnaire-id :edit-answer-state field] #(apply op % args))
             (assoc-in [:question-lab questionnaire-id :edit-answer-state :dirty?] true))}))

(defn update-edit-answer-state-field
  [questionnaire-id field op applied-analysis-version-ids]
  (rf/dispatch [::update-edit-answer-state-field questionnaire-id field op applied-analysis-version-ids]))

(rf/reg-event-fx
  ::save-answer
  (fn [{:keys [db]} [_ questionnaire-id question]]
    (let [{:keys [single-select-options multi-select-options] :as edit-answer-state} (get-in db [:question-lab questionnaire-id :edit-answer-state])
          question-id (:id question)]
      {:http-xhrio {:method :post
                    :uri (str "/api/questions/question/" question-id "/answer")
                    :params (-> edit-answer-state
                                (select-keys [:response :comment :analysis-content :applied-analysis-version-ids])
                                (assoc :options (map (fn [id]
                                                       {:id id})
                                                     (concat single-select-options multi-select-options))
                                       :source-system "webapp"))
                    :format http-config/transit-request-format
                    :response-format http-config/transit-response-format
                    :on-success [::save-answer-success questionnaire-id question]
                    :on-failure [::ui/set-error "Failed to create and select answer"]}})))

(rf/reg-event-fx
 ::save-answer-success
 (fn [{:keys [db]} [_ questionnaire-id question answer]]
   {:db (assoc-in db [:question-lab questionnaire-id :edit-answer-state :dirty?] false)
    :dispatch [::answers/add-and-select-answer questionnaire-id question answer]}))

(defn get-question [db questionnaire-id question-id]
  (->> (get-in db [:questionnaires questionnaire-id :questions :data])
       (filter #(= question-id (:id %)))
       first))

(rf/reg-event-db
 ::set-edit-answer-state
 (fn [db [_ questionnaire-id edit-state]]
   (assoc-in db [:question-lab questionnaire-id :edit-answer-state] edit-state)))

(rf/reg-event-fx
  ::select-answer
  (fn [_ [_ questionnaire-id question-id answer]]
    {:http-xhrio {:method :put
                  :uri (str "/api/questions/question/" question-id "/answer")
                  :params {:answer-id (:id answer)}
                  :format http-config/transit-request-format
                  :response-format http-config/transit-response-format
                  :on-success [::set-answer-selected questionnaire-id question-id answer]
                  :on-failure [::ui/set-error "Failed to select answer"]}}))

(rf/reg-event-fx
  ::set-answer-selected
  (fn [{:keys [db]} [_ questionnaire-id question-id answer selection-data]]
    (let [answer (assoc answer
                        :selected-by (:author selection-data)
                        :selected-at (:created selection-data))
          db (update-in db [:questionnaires
                            questionnaire-id
                            :questions
                            :data]
                        (fn [questions]
                          (mapv (fn [question]
                                  (if (= (:id question) question-id)
                                    (assoc question :selected-answer answer)
                                    question))
                                questions)))
          edit-state (if (= (:source answer) "human")
                       (make-edit-state
                        (get-question db questionnaire-id question-id)
                        answer
                        (analysis.db/db->applied-analyses-for-answer db (:id answer)))
                       ;; Leave answer inputs empty for non-human answer (inference search results)
                       empty-edit-state)]
      {:db db
       :fx [[:dispatch [::set-edit-answer-state questionnaire-id edit-state]]
            [:dispatch [::notifications/add {:message "Answer selected"}]]]})))

(rf/reg-event-fx
 ::try-close-modal
 (fn [{:keys [db]} [_ questionnaire-id]]
   (let [editor-dirty? (common/editor-dirty? db questionnaire-id)]
     {:dispatch (if editor-dirty?
                  [::ui/push-modal {:component (confirm-unsaved-answer {:on-confirm #(rf/dispatch [::ui/pop-modal])})}]
                  [::ui/pop-modal])})))

(rf/reg-event-fx
 ::show-browse-applied-analyses-modal
 (fn [_ [_ applied-analysis-version-ids on-submit question]]
   {:fx [[:dispatch
          [::ui/push-modal
           {:component question.analysis.search/search-analysis-by-category-modal
            :ctx {:applied-analysis-version-ids applied-analysis-version-ids
                  :category nil
                  :on-click-construct (partial analysis.view/show-editor-modal :view)
                  :on-click-state (fn [cid asa]
                                    (analysis.view/show-editor-modal
                                     (if (:next-version-id asa) :view :edit)
                                     cid
                                     asa))
                  :on-submit (fn [& args]
                               (apply on-submit args)
                               (rf/dispatch [::ui/pop-modal]))
                  :question question}
            :content-props {:style {:min-width "75vw" :max-height "90vh" :padding 0}}}]]]}))

(rf/reg-event-fx
 ::show-question-lab-modal
 (fn [{:keys [db]} [_ QuestionLabModal {:keys [questionnaire-id] question-id :id :as question} question-ids]]
   (let [index (->> question-ids
                    (map-indexed vector)
                    (filter (fn [[_ id]]
                              (= id question-id)))
                    ffirst)
         selected-answer (->> (get-in db [:questionnaires questionnaire-id :questions :data])
                              (filter #(= question-id (:id %)))
                              first
                              :selected-answer)]
     {:db (-> db
              (common/set-question-index questionnaire-id index)
              (common/set-question-ids questionnaire-id question-ids))
      :fx [[:dispatch [::answers/get-answers questionnaire-id question-id]]
           [:dispatch [::answer-scope/get]]
           (let [edit-state (if (and selected-answer
                                     (= (:source selected-answer) "human"))
                              (make-edit-state
                               question
                               selected-answer
                               (analysis.db/db->applied-analyses-for-answer
                                db
                                (:id selected-answer)))
                               ;; Leave answer inputs empty for non-human answer (inference search results)
                              empty-edit-state)]
             [:dispatch [::set-edit-answer-state questionnaire-id edit-state]])
           [:dispatch [::ui/push-modal
                       {:ctx {:questionnaire-id questionnaire-id}
                        :component QuestionLabModal
                        :keybinding-overrides {"Escape" #(rf/dispatch [::try-close-modal questionnaire-id])}
                        :content-props {:style {:width "98vw" :height "98vh" :padding 0}}}]]]})))